/**
 * Wagent 知识库的 agent 接入（同一服务器上的 /kb/，ADR-0015）：
 * - 顶部提示：有 agent 申请连接知识库时，核对配对码后点“同意”（只在已配对的设备上出现）；
 * - 设置页：已接入的 agent 列表，可撤销；以及别的电脑怎么接入。
 * 知识库不在这台机器上（例如 Mac 本机开发）时这些接口不存在，界面什么都不显示。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot } from "lucide-react";
import { Button, Card, ErrorNote } from "@/shared/ui";
import { useDevices } from "./PairingRequests";

type KbState = {
  requests: { id: string; code: string; name: string; client: string; address: string }[];
  agents: { name: string; created: string; last_used: string }[];
};

const key = ["kb", "requests"] as const;

async function kbFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`/kb/${path}`, {
    credentials: "same-origin",
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
  });
}

function useKb(enabled: boolean, poll: number | false) {
  return useQuery({
    queryKey: key,
    enabled,
    refetchInterval: poll,
    retry: false,
    queryFn: async (): Promise<KbState | null> => {
      const r = await kbFetch("requests");
      // 404：这台机器上没有知识库；403：这台设备没配对（不该出现，因为 enabled 只在已配对时）
      // 没有知识库时这个地址落到网页本身（HTML），不是 JSON
      const json = r.headers.get("content-type")?.includes("application/json");
      return r.ok && json ? ((await r.json()) as KbState) : null;
    },
  });
}

/** 顶部提示：agent 申请连接知识库。 */
export function KbRequests() {
  const qc = useQueryClient();
  const { owner } = useDevices();
  const kb = useKb(owner, 5000);
  const decide = useMutation({
    mutationFn: async ({ id, approve }: { id: string; approve: boolean }) => {
      const r = await kbFetch(`requests/${id}/decide`, { method: "POST", body: JSON.stringify({ approve }) });
      if (!r.ok) throw new Error(((await r.json()) as { error?: string }).error ?? "处理失败");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
  const pending = kb.data?.requests ?? [];
  if (!pending.length) return null;
  return (
    <div
      role="alert"
      className="flex flex-col gap-2 border-b border-signal-line bg-signal-soft px-4 py-2.5 text-[13.5px]"
    >
      {pending.map((q) => (
        <div key={q.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <Bot className="size-4 text-signal-ink" />
          <span>
            agent “{q.name}”（{q.client}）申请连接你的知识库，配对码{" "}
            <b className="font-mono tracking-widest">{q.code}</b>。和 agent 上显示的一致才同意。
          </span>
          <span className="ml-auto flex gap-2">
            <Button
              size="sm"
              variant="signal"
              busy={decide.isPending}
              onClick={() => decide.mutate({ id: q.id, approve: true })}
            >
              同意
            </Button>
            <Button size="sm" variant="ghost" onClick={() => decide.mutate({ id: q.id, approve: false })}>
              拒绝
            </Button>
          </span>
        </div>
      ))}
      {decide.error ? <ErrorNote>{decide.error.message}</ErrorNote> : null}
    </div>
  );
}

/** 设置页：已接入知识库的 agent。 */
export function KbAgentsCard() {
  const qc = useQueryClient();
  const { owner } = useDevices();
  const kb = useKb(owner, false);
  const revoke = useMutation({
    mutationFn: async (name: string) => {
      const r = await kbFetch(`agents/${encodeURIComponent(name)}`, { method: "DELETE" });
      if (!r.ok) throw new Error(((await r.json()) as { error?: string }).error ?? "撤销失败");
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
  if (!kb.data) return null;
  const url = `${location.origin}/kb/`;
  return (
    <Card className="flex flex-col gap-3 p-5 text-[13.5px]">
      <h2 className="font-semibold">知识库的 agent 接入</h2>
      <p className="text-text-2">
        让别的电脑上的 agent（Codex、Claude Code 等）读一下{" "}
        <code className="select-all rounded bg-surface-2 px-1.5 py-0.5 text-text">{url}</code>
        ，它会按说明自己申请、配置和测试；第一次申请时，这里的页面顶部会出现配对码，核对后点“同意”。
      </p>
      <div className="flex flex-col gap-2">
        <div className="text-[12.5px] text-text-3">已接入的 agent</div>
        {kb.data.agents.length === 0 ? <div className="text-text-2">还没有。</div> : null}
        {kb.data.agents.map((a) => (
          <div key={a.name} className="flex flex-wrap items-center gap-2">
            <Bot className="size-4 text-text-3" />
            <span className="font-medium">{a.name}</span>
            <span className="text-[12px] text-text-3">
              {a.last_used ? `最近使用 ${a.last_used.replace("T", " ")}` : "还没用过"}
            </span>
            <Button
              size="sm"
              variant="danger"
              className="ml-auto"
              busy={revoke.isPending && revoke.variables === a.name}
              onClick={() => revoke.mutate(a.name)}
            >
              撤销
            </Button>
          </div>
        ))}
        {revoke.error ? <ErrorNote>{revoke.error.message}</ErrorNote> : null}
      </div>
    </Card>
  );
}
